import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SensorsIcon from '@mui/icons-material/Sensors';
import { Card, IconButton, Paper, SvgIcon, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { ReactComponent as InsightIcon } from 'assets/images/icons/insight.svg';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useRef } from 'react';
import { brandColors } from 'types/colors';

const timeZone = 'Asia/Jerusalem';
function formatUnixTime(unixTime) {
  // Convert Unix time (seconds) to milliseconds for JavaScript Date object
  const date = new Date(unixTime * 1000);

  // Convert the Date to the specified time zone
  const zonedDate = toZonedTime(date, timeZone);

  // Format the date for display
  const formattedDate = format(zonedDate, 'HH:mm', {
    timeZone,
  });
  return formattedDate;
}

function hoursBetween(unix1, unix2) {
  // convert both to milliseconds
  const ms1 = unix1 * 1000;
  const ms2 = unix2 * 1000;

  // calculate difference in milliseconds
  const diffMs = Math.abs(ms2 - ms1);

  // convert to hours
  const diffHours = diffMs / (1000 * 60 * 60);

  // round to nearest integer
  return Math.round(diffHours);
}

function formatDate(unixSeconds) {
  const date = new Date(unixSeconds * 1000); // convert seconds to milliseconds
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // months are 0-based
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
}

// TODO: sort the sensors list by the sensor with the longest insight time.
// add the sum of hours of each insight to the insights summary. for example:
//  if sensor1: 2 hours of increase & sensor2 1 hour increase, then: in the summary: 3 hours increase.

const Insight = props => {
  const infoContainer = useRef(null);
  // console.log('INSIGHT!! - ', props.insightLabel);
  const handleInsightInfoClick = () => {
    if (infoContainer.current) {
      const box = infoContainer.current;
      if (box.style.display === 'none' || !box.style.display) {
        box.style.display = 'block'; // Make the Box visible
        requestAnimationFrame(() => {
          box.style.opacity = '1';
        });
      } else {
        box.style.opacity = '0'; // Fade out
        setTimeout(() => {
          box.style.display = 'none'; // Set to none after fade-out
        }, 300); // Match the CSS transition duration
      }
    }
  };

  // console.log('INSIGHT!! - props: ', props);

  const RenderTimeRanges = ({ unixTimes, sensorId }) => {
    if (!Array.isArray(unixTimes) || unixTimes.length === 0) {
      return <p>No data available</p>;
    }

    const timeRanges = [];
    timeRanges.push({ start: unixTimes[0] - 3600 });

    for (let i = 1; i < unixTimes.length; i++) {
      let currInsightTime = unixTimes[i];
      let prevInsightTime = unixTimes[i - 1];
      let currLastTimeRangeObj = timeRanges[timeRanges.length - 1];

      if (currInsightTime - 3700 > prevInsightTime) {
        if (currLastTimeRangeObj.end) {
          timeRanges.push({ start: currInsightTime - 3600 });
        } else {
          if (currInsightTime - 3700 > currLastTimeRangeObj.start) {
            // prettier-ignore
            timeRanges[timeRanges.length - 1].end = currLastTimeRangeObj.start + 3600;
            timeRanges.push({ start: currInsightTime - 3600 });
          }
        }
      } else {
        timeRanges[timeRanges.length - 1].end = currInsightTime;
      }
    }
    if (!timeRanges[timeRanges.length - 1].end) {
      timeRanges[timeRanges.length - 1].end =
        timeRanges[timeRanges.length - 1].start + 3600;
    }

    // console.log('timeRanges: ', timeRanges);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {timeRanges.map((range, index) => (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              marginBottom: '.5rem',
            }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                marginLeft: '1rem',
              }}>
              <AccessTimeIcon
                sx={{
                  scale: '1.1',
                  marginBottom: '-.2rem',
                  marginRight: '.5rem',
                  color: '#6E6E6E',
                }}
                className="HomeIconClass"
              />
              <Box sx={{ marginLeft: '1rem' }}>
                <Typography sx={{ fontSize: '1.05rem', fontWeight: '500' }}>
                  {formatUnixTime(range.start)} - {formatUnixTime(range.end)}
                </Typography>
                <Typography sx={{ fontSize: '.85rem', color: '#6E6E6E' }}>
                  {formatDate(range.start)}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}>
              <Typography
                sx={{
                  fontSize: '1rem',
                  color: '#6E6E6E',
                  marginRight: '2rem',
                  fontWeight: '500',
                }}>
                {hoursBetween(range.start, range.end)}h
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const getInsightsAvgHoursNum = () => {
    if (!props.data || !Array.isArray(props.data) || props.data.length === 0) {
      return 0;
    }

    let hoursSum = props.data.reduce(
      (sum, obj) => sum + (obj?.unix_time?.length || 0),
      0,
    );
    return Math.round(hoursSum / props.data.length);
  };

  // console.log('data:', props.data);

  return (
    <Paper
      sx={{
        // paddingX: 2,
        // paddingY: 1,
        marginY: 2,
        minHeight: '4rem',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '.5rem',
      }}>
      <Box sx={{ overflowY: 'auto', overflow: 'hidden', width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', padding: '1rem' }}>
            <SvgIcon
              sx={{
                scale: '2.5',
                marginRight: '0.5rem',
                marginRight: '1rem',
              }}
              inheritViewBox>
              <InsightIcon
                className="HomeIconClass"
                style={{ fill: props.iconColor }}
              />
            </SvgIcon>
            <Box>
              <Typography fontSize="medium">{props.insightLabel}</Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}>
                <Typography
                  sx={{ fontSize: '.85rem', color: '#6E6E6E' }}
                  fontSize="medium">
                  Average number of hours:
                </Typography>
                <Typography
                  sx={{
                    marginLeft: '.5rem',
                    fontWeight: '600',
                    fontSize: '.9rem',
                    color: '#6E6E6E',
                  }}>
                  ~ {getInsightsAvgHoursNum()}
                </Typography>
              </Box>
            </Box>
          </Box>
          <IconButton
            fontSize="meduim"
            style={{ color: 'black' }}
            onClick={handleInsightInfoClick}>
            <ArrowDropDownIcon />
          </IconButton>
        </Box>
        <Box
          className="insight-sensors-info"
          sx={{
            display: 'none', // Initially hidden
            opacity: 0,
            transition: 'opacity 0.3s ease',
          }}
          ref={infoContainer}>
          {props.data.map((sensor, index) => {
            const prev = index > 0 ? props.data[index - 1] : null;

            return sensor.sensor_name ===
              props.data[index + 1]?.sensor_name ? null : (
              <Box
                sx={{
                  //   justifyContent: 'space-between',
                  display: 'flex',
                  flexDirection: 'column',
                  borderTop: '1px solid rgb(209, 209, 209)',
                  padding: '1rem',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 0.5,
                  }}>
                  <Typography sx={{ fontSize: '1.1rem' }}>
                    <LocationOnIcon
                      sx={{
                        scale: '1.1',
                        marginBottom: '-.2rem',
                        marginRight: '.4rem',
                      }}
                      className="HomeIconClass"
                    />
                    Location
                  </Typography>
                  <Card>
                    <Typography
                      sx={{
                        fontWeight: '400',
                        fontSize: '1.1rem',
                        backgroundColor: '#ebebebff',
                        borderRadius: '6px',
                        paddingX: 1,
                      }}>
                      {sensor.building} / {sensor.floor_number} /{' '}
                      {sensor.area_name}
                    </Typography>
                  </Card>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 0.5,
                  }}>
                  <Typography sx={{ fontSize: '1.1rem' }}>
                    <SensorsIcon
                      sx={{
                        scale: '1.1',
                        marginBottom: '-.2rem',
                        marginRight: '.5rem',
                      }}
                      className="HomeIconClass"
                    />
                    Sensor
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: '400',
                      fontSize: '1.1rem',
                    }}>
                    {sensor.sensor_name}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 0.5,
                  }}>
                  <Typography sx={{ fontSize: '1.1rem' }}>
                    <AccessTimeFilledIcon
                      sx={{
                        scale: '1.1',
                        marginBottom: '-.2rem',
                        marginRight: '.5rem',
                      }}
                      className="HomeIconClass"
                    />
                    Total hours
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: '600',
                      fontSize: '1.1rem',
                      color: brandColors.BLUE,
                    }}>
                    {sensor.unix_time.length}h
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: 2,
                  }}>
                  <RenderTimeRanges
                    unixTimes={sensor.unix_time}
                    sensorId={sensor.sensor_id}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
};

export default Insight;
